import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import moment from "moment";
import Swal from "sweetalert2";
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField,
  Stack,
} from '@mui/material';
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { setProjectDetail } from "../../store/modules/appState/actions";
import FinancialHandler from '../../handlers/FinancialHandler';
import { handleBuildProject } from './HandleBuildProject';
import { StyledTextarea } from './Helpers';

const AddProjectModal = ({
  open,
  onClose,
  setLoading,
  projectChange,
  company,
  user,
  appState,
  dispatchHandleBuildProject,
  dispatchSetProjectDetail,
}) => {
  const [startDate, setStartDate] = useState(appState.dataSettings.startDate || moment.utc(new Date()).add({ months: -1 }));
  const [endDate, setEndDate] = useState(appState.dataSettings.endDate || moment.utc(new Date()).add({ months: -1 }));
  const [form, setForm] = useState({ projectName: '', description: '' });
  const [projectReady, setProjectReady] = useState(false);

  const handleClick = async () => {
    let startDateObj = moment.utc(startDate);
    let endDateObj = moment.utc(endDate);
    let earlistDateObj = moment.utc(company.companyInfo?.earlistDate);   //it is the first day of the month 00:00.
    let recentDateObj = moment.utc(company.companyInfo?.latestDate);   //get the latest time of the month

    if(startDateObj.isBefore(earlistDateObj)) {
      //start date error handling
      // Swal.fire({
      //   icon: "info",
      //   title: "Start Date",
      //   text: "The start date is not valid. Please check again for available time range."
      // });
      toast.error("The start date is not valid. Please check again for available time range.");
      setStartDate(earlistDateObj);
      if(endDateObj.isBefore(earlistDateObj)) setEndDate(earlistDateObj);
        //in case ending date < starting date
    }
    else if(endDateObj.isAfter(recentDateObj.clone().add({ days: 1 }))) {
      //end date error handling
      // Swal.fire({
      //   icon: "info",
      //   title: "End Date",
      //   text: "The end date is not valid. Please check again for available time range."
      // });
      toast.error("The end date is not valid. Please check again for available time range.");
      if(startDateObj.isAfter(recentDateObj.clone().add({ days: 1 }))) setStartDate(recentDateObj);
        //in case starting date > ending date
      setEndDate(recentDateObj);
    }
    else if(endDateObj.isBefore(startDateObj.clone().add({ months: -1 }))) {
      //time gap mustn't be minus
      // Swal.fire({
      //   icon: "info",
      //   title: "The Start And End Date",
      //   text: "The start date must be later than the end date."
      // });
      toast.error("The start date must be later than the end date.");
      setEndDate(startDateObj);
    }
    else if(endDateObj.diff(startDateObj, 'years', true) >= 5) {
      //time gap mustn't be bigger than 5 years
      // Swal.fire({
      //   icon: "info",
      //   title: "Time Gap",
      //   text: "The time gap between the start and end dates must be smaller than 5 years."
      // });
      toast.error("The time gap between the start and end dates must be smaller than 5 years.");
      setEndDate(startDateObj.clone().add({ years: 5 }).add({ months: -1 }));
    }
    else if(endDateObj.isAfter(moment.utc(new Date()))) {
      //end date shouldn't be after today
      // Swal.fire({
      //   icon: "info",
      //   title: "Time Gap",
      //   text: "The end date should be before the current month."
      // });
      toast.error("The end date should be before the current month.");
      setEndDate(startDateObj);
    }
    else if (endDateObj && endDateObj.isValid() && startDateObj.isValid()) {
      setLoading(true);
      onClose();
      await FinancialHandler.saveProjectData();   //save current project before create new
      await dispatchHandleBuildProject({ startDate: startDateObj, endDate: endDateObj, company, form });
      setProjectReady(true);
      setLoading(false);
    }
  }

  useEffect(async () => {
    if(projectReady) {
      setLoading(true);
      const projectDetail = await FinancialHandler.saveProjectData({ appState, user }, "PARTIAL");
        //save project data to the backend, fetch and implement project id and etc.
      await projectChange(projectDetail.projectId, company.id);
      dispatchSetProjectDetail(projectDetail);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Your project is created successfully!"
      });
      setProjectReady(false);
      setLoading(false);
    }
  }, [projectReady]);

  useEffect(() => {
    //initialize form everytime user opens the modal
    if (open) setForm({ projectName: '', description: '' });
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* <DialogTitle display="flex" justifyContent="space-between">
        <>Add New Project{company.companyName ? ` for ${company.companyName}` : ' '}</>
        {company.id === "sandbox" && <FormControlLabel
          control={
            <Checkbox
              checked={form.manuality}
              onChange={(e) => setForm((s) => ({ ...s, manuality: e.target.checked }))}
              color="primary"
            />
          }
          label="Manual"
          className="block"
        />}
      </DialogTitle> */}
      <DialogTitle>Add New Project{company.companyName ? ` for ${company.companyName}` : ' '}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl fullWidth required>
            <FormLabel>Project Name</FormLabel>
            <StyledTextarea
              minRows={1}
              value={form.projectName}
              onChange={(e) => setForm((s) => ({ ...s, projectName: e.target.value }))}
              aria-label="Project Name"
              placeholder="Enter project name"
              required
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>Project Description</FormLabel>
            <StyledTextarea
              minRows={3}
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              aria-label="Project Description"
              placeholder="Enter project description"
            />
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} className='pl-10'>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                disableFuture
                onError={() => {
                  console.log("There are errors");
                }}
                views={["year", "month"]}
                openTo="year"
                label="Start Date"
                value={moment.utc(startDate)}
                onChange={(newValue) => {
                  setStartDate(newValue);
                }}
                renderInput={(params) => (
                  <TextField style={{backgroundColor: "transparent"}} sx={{ width: 300 }} {...params} variant="outlined" />
                )}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                disableFuture
                onError={() => {
                  console.log("There are errors");
                }}
                views={["year", "month"]}
                openTo="year"
                label="End Date"
                value={moment.utc(endDate)}
                onChange={(newValue) => {
                  setEndDate(newValue);
                }}
                renderInput={(params) => (
                  <TextField style={{backgroundColor: "transparent"}} sx={{ width: 300 }} {...params} variant="outlined" />
                )}
              />
            </LocalizationProvider>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className='mb-5 mr-5'>
        <Button onClick={onClose} variant="text">Cancel</Button>
        <Button onClick={handleClick} disabled={form.projectName.trim().length < 1} variant="contained">
          Add Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  appState: state.appState,
});

const mapDispatchToProps = (dispatch) => ({
  async dispatchHandleBuildProject({ startDate: startDateObj, endDate: endDateObj, company, form }) {
    await dispatch(handleBuildProject({ startDate: startDateObj, endDate: endDateObj, company, form }));
  },

  async dispatchSetProjectDetail(payload) {
    await dispatch(setProjectDetail(payload));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddProjectModal);
